import { AxiosResponse } from 'axios';

import apiClient from './client';
import { ApiResponseDto } from 'interfaces/apiResponse';
import { Branch } from 'interfaces/branch';

export interface BranchCreateRequest {
  name: string;
  address?: string;
}

export interface BranchUpdateRequest {
  name: string;
  address?: string;
  enabled: boolean;
}

interface BranchesService {
  getBranches(enabled?: boolean): Promise<Branch[]>;
  createBranch(data: BranchCreateRequest): Promise<Branch>;
  updateBranch(id: number, data: BranchUpdateRequest): Promise<Branch>;
  deleteBranch(id: number): Promise<void>;
}

const endpoints = {
  branches: '/branches',
  branch: (id: number) => `/branches/${id}`,
};

export const branchesService: BranchesService = {
  getBranches: async (enabled?: boolean): Promise<Branch[]> => {
    const response: AxiosResponse<ApiResponseDto<Branch[]>> = await apiClient.get(endpoints.branches, {
      params: { enabled },
    });

    if (response.data.errors) {
      throw new Error(response.data.errors!.map(error => error.message).join(', '));
    }

    return response.data.data!;
  },

  createBranch: async (data: BranchCreateRequest): Promise<Branch> => {
    try {
      const response: AxiosResponse<ApiResponseDto<Branch>> = await apiClient.post(endpoints.branches, data);

      if (response.data.errors) {
        throw new Error(response.data.errors!.map(error => error.message).join(', '));
      }

      return response.data.data!;
    } catch (error: any) {
      if (error.response?.data?.errors) {
        const firstError = error.response.data.errors[0];

        if (firstError.code === 'ACTIVE_BRANCH_LIMIT_REACHED' && firstError.details) {
          const { branchLimit } = firstError.details;
          throw new Error(`Έχετε φτάσει το μέγιστο αριθμό ενεργών καταστημάτων (${branchLimit}). Επικοινωνήστε μαζί μας για αναβάθμιση.`);
        }

        throw new Error(firstError.message);
      }

      throw error;
    }
  },

  updateBranch: async (id: number, data: BranchUpdateRequest): Promise<Branch> => {
    const response: AxiosResponse<ApiResponseDto<Branch>> = await apiClient.put(endpoints.branch(id), data);

    if (response.data.errors) {
      throw new Error(response.data.errors!.map(error => error.message).join(', '));
    }

    return response.data.data!;
  },

  deleteBranch: async (id: number): Promise<void> => {
    await apiClient.delete(endpoints.branch(id));
  },
};
